import { getWhiteListedParamValue } from '@mimir/client-common/util/persistentUserSession';
import type { LoadedItem, ClientItem } from '@mimir/common/dto/searchResultHit';
import type { FieldValue } from '@mimir/common/dto/fieldValue';
import type { TimedMetadataCollection } from '@mimir/common/dto/timedMetadataModels';
import { SystemDefaultField } from '@mimir/common/mdf/mdfDefaults';
import ClientMosHelper from '@mimir/common/mos/clientMosHelper';
import type { MosItemReplaceData, MosObjCompatibleItem } from '@mimir/common/mos/mos-interfaces';
import { OBJ_PROXY_PATH_QUERY_PARAM } from '@mimir/common/mos/mos-interfaces';
import { isObject } from '@mimir/primitive/type-guards/isObject';
import cloneDeep from 'lodash/cloneDeep';
import Vue from 'vue';
import { getRootUrl } from '../browserEnv';
import type { RichPostMessageItem, GenericEvent, PostMessageItem, ImportItemEvent, PostMessage, ReceivedMessage, SupportedIncomingAction } from '../model/postMessage';
import { postIncomingMessageActions } from '../model/postMessage';
import { replaceIds } from '../pages/export/export-util';
import { prepareSearchRouterQueryParamsForVersion } from '../pages/search/util';
import { getGlobalRouter } from '@mimir/web/router';
import { getGlobalStore, appState } from '@mimir/web/store';
import { createMosCompatibleItem } from './mosClientUtil';
import { ofItem } from '@mimir/common/aws/api/path';
import { isLoadedItem } from '@mimir/common/search/interfaces';
import { getGlobalServiceDocument } from '@mimir/client-common/globals/backendClient';
import { setAsWorkFolder } from '../globals/folders';
import { showSimpleDialog } from '../dialogs/simple';
import { SafeHtmlBuilder } from '@mimir/common/util/safeHtmlBuilder';
import { isInIframe, type ApplicationContext } from '@mimir/common/context/applicationContext';
import type { CompleteType } from '@mimir/common/util/general';
import { ConsoleLogger } from '@mimir/common/util/logging';
import { storeDispatchOpenItemById } from '../store/storeDispatch';
import type { QuickPreview } from '../model/quickPreviewOps';
import { enableNativeDragData } from '../globals/nativeDrag';
import { enableCrossOriginDrag } from '../globals/crossOriginDrag';
import { searchState } from '@mimir/web/globals/searchState';
import { clearFilters } from '@mimir/common/filters/Filters';
import { doCleanSearch } from './doCleanSearch';
import { markActivity } from '@mimir/web/globals/activityTracker';

/**
 * Generate a message to let hosts know the user has logged in.
 */
export const generateUserLoggedInJSON = () => {
  const loginMessage = {
    source: 'MIMIR',
    event: 'new_login'
  };
  return JSON.stringify(loginMessage);
};

const getValueAsString = (f: FieldValue): string => {
  if (f === undefined || f === null || typeof f !== 'string') {
    return '';
  }
  return f as string;
};

/**
 * Generate drag information for the given item.
 */
export const generatePostMessageItem = (item: ClientItem | LoadedItem): PostMessageItem => {
  // These default fields are known to be strings when they are defined, so let's
  // treat them as such
  const title = getValueAsString(item.metadata.formData[SystemDefaultField.Title]);
  const mediaCreatedOn = getValueAsString(item.metadata.formData[SystemDefaultField.MediaCreatedOn]);
  const itemCreatedOn = getValueAsString(item.metadata.formData[SystemDefaultField.CreatedOn]);
  const description = getValueAsString(item.metadata.formData[SystemDefaultField.Description]);

  let thumbnail = item.thumbnail;
  // If we detect a resolved thumbnail url matching our placeholder img we overwrite the thumbnail with URL
  // so 3rd party integrators can use this image instead of needing to implement similar logic
  if (item.resolvedThumbnailUrl?.endsWith('missing-item.png')) {
    thumbnail = `${getRootUrl()}/assets/missing-item.png`;
  }

  const clone = cloneDeep(item);
  // Send the full search hit, excluding client properties, including backwards compatibility properties
  const postMessageItem: CompleteType<PostMessageItem> = {
    id: item.id,
    title,
    description,
    itemCreatedOn,
    mediaRecordedOn: mediaCreatedOn,
    provider: 'Mimir',
    self: ofItem(getGlobalServiceDocument().links, item.id),
    metadata: replaceIds(clone.metadata),
    thumbnail,
    createdOn: clone.createdOn,
    itemState: clone.itemState,
    isDeleting: clone.isDeleting || false,
    transcodingState: clone.transcodingState || undefined,
    progressInfo: clone.progressInfo,
    originalFileName: clone.originalFileName,
    itemType: clone.itemType,
    folderParents: clone.folderParents,
    proxy: clone.proxy || undefined,
    archiveFileTransferState: clone.archiveFileTransferState,
    aaf: clone.aaf,
    media: clone.media,
    externalProxyInfo: clone.externalProxyInfo,
    aafLocation: clone.aafLocation,
    ingest: clone.ingest,
    mediaType: clone.mediaType,
    mediaFrameRate: clone.mediaFrameRate,
    editProxyFormat: clone.editProxyFormat,
    modifiedOn: clone.modifiedOn,
    isLive: clone.isLive || false,
    generatedContentLocation: clone.generatedContentLocation,
    visibleTo: clone.visibleTo,
    updatableBy: clone.updatableBy,
    scheduledRecording: clone.scheduledRecording,
    vttUrl: clone.vttUrl,
    name: clone.name,
    mediaDuration: clone.mediaDuration,
    thumbnailStrip: clone.thumbnailStrip,
    ingestSourceFullPath: clone.ingestSourceFullPath,
    ingestSourceS3Bucket: clone.ingestSourceS3Bucket,
    ingestSourceStorageId: clone.ingestSourceStorageId,
    createdBy: clone.createdBy,
    owner: clone.owner,
    languageCode: clone.languageCode,
    isArchived: clone.isArchived || false,
    geoLocation: clone.geoLocation,
    virtualClipInfo: clone.virtualClipInfo,
    warnings: clone.warnings,
    hasProxy: clone.hasProxy || false,
    hasEditProxy: clone.hasEditProxy || false,
    mediaSize: clone.mediaSize,
    proxySize: clone.proxySize,
    parent: clone.parent,
    mediaWidth: clone.mediaWidth,
    mediaHeight: clone.mediaHeight,
    hasRestrictedMaterial: clone.hasRestrictedMaterial,
    usageRules: clone.usageRules,
    exiftags: clone.exiftags,
    technicalMetadata: clone.technicalMetadata,
    editProxyUrl: clone.editProxyUrl,
    highRes: clone.highRes,
    srtUrl: isLoadedItem(clone) ? clone.srtUrl : undefined,
    translationSrtUrl: isLoadedItem(clone) ? clone.translationSrtUrl : undefined,
    translationVttUrl: isLoadedItem(clone) ? clone.translationVttUrl : undefined,
    livePreviewLocation: clone.livePreviewLocation,
    detectedLabels: isLoadedItem(clone) ? clone.detectedLabels : undefined,
    detectedPersons: isLoadedItem(clone) ? clone.detectedPersons : undefined,
    detectedCelebrities: isLoadedItem(clone) ? clone.detectedCelebrities : undefined,
  };

  return postMessageItem;
};

export const generateRichPostMessageItem = (item: LoadedItem, timedMetadata: TimedMetadataCollection | null): RichPostMessageItem => {
  const postMessageItem = generatePostMessageItem(item);
  return {
    ...postMessageItem,
    timedMetadata,
  };
};


/**
 * Send a message to the parent window.
 *
 * TODO - replace all manual occurrances of postMessage with this entry point for better type safety.
 * @param data Data to send over the write
 * @param action Which action it denotes
 */
export const postMessageToParentWindow = (event: ImportItemEvent | GenericEvent) => {
  const store = getGlobalStore();
  const targetOrigin = store.state.postMessageOrigin;
  const user = store.state.user.email;
  const msg: PostMessage = {
    source: 'MIMIR',
    action: event.action,
    targetOrigin,
    user,
    payload: event.payload
  };
  window.parent.postMessage(msg, targetOrigin);
};

export const notifyMimirIsLoaded = () => {
  const store = getGlobalStore();
  const targetOrigin = store.state.postMessageOrigin;
  const user = store.state.user.email;
  if (appState.mosHelper !== null) {
    appState.mosHelper.notifyHost();
  } else {
    const loadedMsg: PostMessage = {
      source: 'MIMIR',
      action: 'mimir_loaded',
      targetOrigin,
      user
    };
    window.parent.postMessage(loadedMsg, targetOrigin);
    store.commit('notifiedHostingFrame');
  }
};

export const trySetUpPostMessageRelatedState = (applicationContext: ApplicationContext) => {
  const shouldConsiderListening = isInIframe(applicationContext);
  if (shouldConsiderListening) {
    // To access a new param from route, the param should be enlisted in whiteListedParams in persistentUserSession.ts.
    const originQueryParam = getWhiteListedParamValue('origin');
    const isMos = getWhiteListedParamValue('mos') !== null;
    const customMosId = getWhiteListedParamValue('mosId');
    const useNcsItem = getWhiteListedParamValue('ncsItem');
    const iopp = getWhiteListedParamValue('iopp');
    const includeObjProxyPath = getWhiteListedParamValue(OBJ_PROXY_PATH_QUERY_PARAM);
    const store = getGlobalStore();
    if (isMos) {
      // In MOS protocol it is unacceptable to not have an origin, so we bail out and inform the user.
      if (!originQueryParam) {
        // This is called before the UI is drawn and ready to show dialogs. We use appState to indicate to the UI once it is
        // mounted to run this function which will show the dialog once it can.
        appState.runFnOnLayoutLoad = () =>
          showSimpleDialog({
            closeOnActionClick: true,
            icon: 'stop',
            maxWidth: 400,
            header: 'Missing origin',
            baseHtml: new SafeHtmlBuilder()
              .addText('Detected MOS mode, but did not find a newsroom origin to send messages to. Please include ')
              .addBoldText('origin=https://your-origin')
              .addText(' as a query parameter to the iframe src url. Example: ')
              .addLineBreakAndAppendText('')
              .addLineBreakAndAppendText('https://mimir.mjoll.no/browse?origin=https://yourorigin.com&mos=true'),
          });
      } else {
        appState.mosHelper = new ClientMosHelper(new ConsoleLogger(), originQueryParam, (data: string, origin: string) => {
          window.parent.postMessage(data, origin);
        }, new DOMParser(), {
          openItemId: (itemId: string, mosItemReplaceData?: MosItemReplaceData | null) => {
            if (mosItemReplaceData !== undefined) {
              store.commit('setMosItemReplaceData', mosItemReplaceData);
            }
            if (appState.mosHelper) {
              appState.mosHelper.mosOpenItemInfo.currentMosOpenItemId = itemId;
              appState.mosHelper.mosOpenItemInfo.mosItemReplaceRequestId = null;
            }
            const routerLinkQueryParams = prepareSearchRouterQueryParamsForVersion(itemId);
            getGlobalRouter().replace({
              name: 'browse',
              query: routerLinkQueryParams,
            });
          },
          requestSelectedItem: (): MosObjCompatibleItem | null => {
            const selectedItems = searchState.reactive.selectedSearchItems;
            if (selectedItems.length !== 1) {
              return null;
            } else {
              const selectedItem = selectedItems[0];
              return createMosCompatibleItem(selectedItem);
            }
          }
        }, useNcsItem === 'true', typeof iopp === 'string' ? iopp : undefined, customMosId ?? undefined, includeObjProxyPath === 'true',
          Vue.observable({ currentMosOpenItemId: null, mosItemReplaceRequestId: null }));
      }
    } else if (originQueryParam) {
      store.commit('setPostMessageOrigin', originQueryParam);
    } else {
      // For JSON API for now we fallback to '*' origin.
      console.warn('No origin found. Please include \'origin=https://your-origin\' as a query parameter to the iframe src url.');
      store.commit('setPostMessageOrigin', '*');
    }

    const crossOriginDragParam = getWhiteListedParamValue('crossOriginDrag');
    if (crossOriginDragParam === 'true') {
      const dragDestination = getWhiteListedParamValue('dragDestination') ?? '';
      enableCrossOriginDrag(dragDestination);
    }
  }
  return shouldConsiderListening;
};

export const listenToPostMessage = (applicationContext: ApplicationContext, quickPreview: QuickPreview) => {
  window.addEventListener('message', ev => messageReceivedHandler(applicationContext, quickPreview, ev), false);
};

const getValidStoreCommit = (action: SupportedIncomingAction) => {
  if (action === 'enable_rich_payloads') return 'enableRichPayloads';
  if (action === 'enable_native_drag_data') return 'enableNativeDragData';
  return null;
};

const messageReceivedHandler = (
  applicationContext: ApplicationContext,
  quickPreview: QuickPreview,
  ev: MessageEvent
) => {
  const messageData: unknown = ev.data;
  if (appState.mosHelper !== null) {
    appState.mosHelper.mosMessageFromHost(ev);
  } else if (isRecognizedMessagePayload(messageData)) {
    if (!postIncomingMessageActions.includes(messageData.action)) {
      console.warn('Did not recognize action ' + messageData.action);
      return;
    }

    const store = getGlobalStore();
    const configCommit = getValidStoreCommit(messageData.action);
    if (configCommit) {
      if (store.state.notifiedHostingFrame) {
        if (configCommit === 'enableRichPayloads') {
          store.commit(configCommit);
          console.log(`${configCommit} enabled!`);
        } else if (configCommit === 'enableNativeDragData') {
          enableNativeDragData();
          console.log(`${configCommit} enabled!`);
        }
      } else {
        console.warn(
          'Please wait until Mimir has sent a mimir_loaded event before sending messages'
        );
      }
      return;
    }

    markActivity();

    if (messageData.action === 'request_item') {
      const currentItem = store.state.timeline.currentItem || quickPreview.state.currentItem.value;
      if (currentItem) {
        const postMessageItem = generatePostMessageItem(currentItem);
        const richMode = store.getters.useRichPayloadsForPostmessage as boolean;
        const response = richMode ? postMessageItem : JSON.stringify(postMessageItem);
        window.parent.postMessage(response, store.state.postMessageOrigin);
      }
      return;
    }

    if (!messageData.payload) {
      console.warn('Action requires payload in message ', messageData);
      return;
    }

    switch (messageData.action) {
      case 'search': doCleanSearch(searchState, store.dispatch, messageData.payload); break;
      case 'sticky_search': searchState.ops.doStickySearch(messageData.payload, (clearSticky: boolean) => clearFilters({ filters: store.state.filters, clearSticky })); break;
      case 'open_item': storeDispatchOpenItemById({ applicationContext, itemId: messageData.payload, returnTo: null, quickPreview }); break;
      case 'open_folder': store.dispatch('openFolder', messageData.payload); break;
      case 'set_work_folder': void setAsWorkFolder(messageData.payload); break;
      default: {
        // Shouldn't happen
      }
    }
  }
};

function isRecognizedMessagePayload(obj: unknown): obj is ReceivedMessage {
  if (isValidMessagePayload(obj)) {
    return 'MIMIR' === obj.destination.toUpperCase();
  }
  return false;
}

function isValidMessagePayload(obj: unknown): obj is ReceivedMessage {
  if (!obj) return false;
  if (isObject(obj)) {
    const probablyReceivedMessage = obj as Partial<ReceivedMessage>;
    return probablyReceivedMessage.destination !== undefined &&
      probablyReceivedMessage.action !== undefined;
  }
  return false;
}
